"use client";

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import {
  Sparkles, QrCode, Link as LinkIcon, Zap, HelpCircle,
  Smartphone, Share2, CheckCircle2, ArrowRight, Users
} from 'lucide-react';
import TutorialCarousel from '@/components/tutorial/TutorialCarousel';

const EXAMPLE_USERNAMES = [
  'dr-alvarez', 'ing-ernesto', 'foto-sofia', 'tacos-el-torito',
  'coach-mariela', 'juan-dev', 'estudio-arteva', 'clinica-montoya',
  'paula-realty', 'barber-shop',
];

const USER_BASE_DOMAIN = process.env.NEXT_PUBLIC_USER_SNAPSITE_BASE_URL || 'snapsite.cc';

const footerLinks = [
  { href: "/", label: "Inicio" },
  { href: "/login", label: "Iniciar Sesión" },
  { href: "/ayuda", label: "Ayuda" },
  { href: "/privacidad", label: "Privacidad" },
  { href: "/terminos", label: "Términos" },
  { href: "/precios", label: "Precios" },
  { href: "/demo", label: "Live Demo" },
  { href: "/reportar", label: "Reportar contenido" },
  { href: "/changue-log", label: "Novedades" },
];

const features = [
  {
    icon: Zap,
    title: "Lista en 5 Minutos",
    desc: "Nombre, foto, bio y enlaces. Sin código, sin complicaciones.",
  },
  {
    icon: LinkIcon,
    title: "Un Solo Link para Todo",
    desc: "Redes, contacto y proyectos en tu dirección propia — fácil de recordar.",
  },
  {
    icon: QrCode,
    title: "QR Incluido Gratis",
    desc: "Descarga tu QR y tu tarjeta en PNG, con frente y reverso, listos para imprimir o compartir.",
  },
  {
    icon: Smartphone,
    title: "Se guarda en el celular",
    desc: "Quien te visita puede guardarte en su pantalla de inicio y verte sin señal.",
  },
];

const trusts = [
  "Gratis para siempre",
  "Sin tarjeta de crédito",
  "Activa en minutos",
  "Funciona sin señal",
];

const jsonLdWebApp = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "SnapSite",
  "url": "https://snapsite.cc",
  "description": "Crea tu tarjeta digital profesional gratis en minutos. Comparte todos tus links, redes sociales y contacto en una sola página con tu propio subdominio y código QR.",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web",
  "inLanguage": "es-MX",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "MXN",
    "description": "Plan gratuito disponible sin tarjeta de crédito"
  },
};

const jsonLdOrg = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "SnapSite de .Site RTSI",
  "url": "https://snapsite.cc",
  "logo": "https://snapsite.cc/logo_snapsite.png",
};

export default function HomePage() {
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const [usernameIndex, setUsernameIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setUsernameIndex((i) => (i + 1) % EXAMPLE_USERNAMES.length);
        setFadeIn(true);
      }, 300);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <main className="min-h-dvh flex flex-col bg-background overflow-x-hidden">

        {/* ── HERO — con orbes flotantes ── */}
        <section className="relative overflow-hidden">

          {/* ── Capa de orbes / esferas de fondo ── */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0">
            {/* Orbe principal — arriba izquierda */}
            <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-primary/25 blur-3xl" />
            {/* Orbe secundario — arriba derecha */}
            <div className="absolute -top-16 right-0 w-[380px] h-[380px] rounded-full bg-violet-500/20 blur-3xl" />
            {/* Orbe terciario — centro abajo */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-primary/15 blur-3xl" />
            {/* Orbe acento — esquina inferior derecha */}
            <div className="absolute -bottom-20 -right-20 w-[300px] h-[300px] rounded-full bg-indigo-400/20 blur-3xl" />
          </div>

          {/* Nav — sobre los orbes */}
          <nav className="relative z-10 w-full px-6 py-4 flex items-center justify-between max-w-5xl mx-auto">
            {/* SVG (3KB) en vez del PNG (70KB) que usa este logo para favicon/apple-touch-icon
                — a este tamaño (36px) el next/image optimizer no aporta nada y el PNG pesa
                20x más de lo necesario. */}
            <img src="/logo_snapsite.svg" alt="Logo SnapSite" width={36} height={36} className="rounded-md" />
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" asChild className="backdrop-blur-sm">
                <Link href="/login">Iniciar Sesión</Link>
              </Button>
              <Button size="sm" asChild>
                <Link href="/dashboard">
                  Crear gratis
                  <ArrowRight className="ml-1.5 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </nav>

          {/* Hero content */}

         <div className="relative z-10 flex flex-col items-center justify-center text-center px-4 pt-10 pb-20 md:pt-16 md:pb-28 max-w-3xl mx-auto w-full gap-6">
  
  {/* Marca principal */}
  <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-foreground tracking-tight leading-tight">
    SnapSite
  </h1>

  {/* Badge más sutil */}
   <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/50 text-primary-foreground text-sm shadow-md"> <Users className="h-3.5 w-3.5" /> Tarjeta digital para profesionales </div>

  {/* Subheadline */}
  <div className="space-y-3">
    <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground leading-snug">
      Tu tarjeta digital
      <span className="block text-primary font-bold">
        profesional y gratis
      </span>
    </h2>

    <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
      Comparte todos tus links, contacto y redes sociales en un solo lugar — con tu propio QR incluido.
    </p>
  </div>


            <div className="flex flex-col items-center gap-2">
              <p className="text-xs text-muted-foreground uppercase tracking-widest font-medium">Tu perfil vivirá en</p>
              <div className="flex items-center gap-1 bg-card border-2 border-border rounded-xl px-5 py-2.5 font-mono text-sm md:text-base shadow-lg">
                <span
                  style={{ transition: 'opacity 0.3s ease', opacity: fadeIn ? 1 : 0 }}
                  className="text-primary font-bold"
                >
                  {EXAMPLE_USERNAMES[usernameIndex]}
                </span>
                <span className="text-foreground/60 font-medium">.{USER_BASE_DOMAIN}</span>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center gap-3 w-full max-w-sm">
              <Button asChild size="lg" className="w-full group text-base h-12 shadow-lg shadow-primary/20">
                <Link href="/dashboard">
                  <Sparkles className="mr-2 h-5 w-5 transition-transform group-hover:scale-110" />
                  Crear mi Perfil Gratis
                </Link>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="w-full h-12 bg-background/70 backdrop-blur-sm"
                onClick={() => setIsTutorialOpen(true)}
              >
                <HelpCircle className="mr-2 h-4 w-4 text-primary" />
                ¿Cómo funciona?
              </Button>
            </div>

            {/* Trust signals */}
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2">
              {trusts.map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                  {t}
                </span>
              ))}
            </div>

            {/* DevMonkeys image — con glass frame */}
            <div className="w-full max-w-sm mx-auto">
              <div className="rounded-2xl overflow-hidden  backdrop-blur-sm p-1">
                <Image
                  src="/devmonkeyshome.webp"
                  alt="Ilustración de monos desarrolladores trabajando en SnapSite"
                  width={400}
                  height={260}
                  className="rounded-xl w-full h-auto"
                  data-ai-hint="monkeys coding"
                  priority
                />
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Creado con ❤️ por nuestros <strong>DevMonKeys</strong> para que tú brilles.
              </p>
            </div>
          </div>
        </section>

        {/* ── FEATURES GRID ── */}
        <section className="relative w-full bg-muted/50 border-t border-border py-16 px-4 overflow-hidden">
          {/* Orbe de fondo sutil */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0">
            <div className="absolute -top-24 left-1/4 w-[400px] h-[400px] rounded-full bg-primary/10 blur-3xl" />
            <div className="absolute -bottom-24 right-1/4 w-[300px] h-[300px] rounded-full bg-violet-500/10 blur-3xl" />
          </div>

          <div className="relative z-10 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-foreground mb-2">
              Todo lo que necesitas, sin pagar nada
            </h2>
            <p className="text-center text-muted-foreground mb-10">
              Médicos, fotógrafos, coaches, restaurantes — cualquier profesional listo en minutos.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {features.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="group p-5 rounded-2xl border bg-background/70 backdrop-blur-sm hover:border-primary/40 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/15 transition-colors">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground mb-1">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>

            {/* WhatsApp highlight */}
            <div className="mt-4 flex items-center gap-4 p-5 rounded-2xl border border-[#25D366]/30 bg-[#25D366]/5 backdrop-blur-sm">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#25D366]/15 flex items-center justify-center">
                <Share2 className="w-5 h-5 text-[#25D366]" />
              </div>
              <div>
                <p className="font-semibold text-foreground text-sm">Compartir por WhatsApp incluido</p>
                <p className="text-sm text-muted-foreground">
                  Cada perfil tiene un botón para mandar tu tarjeta directo al chat de cualquier contacto — con un solo toque.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── SISTEMA DE REFERIDOS — vistoso, para que se comparta el link al verla ── */}
        <section className="relative w-full py-16 px-4 overflow-hidden bg-gradient-to-b from-primary/5 via-transparent to-transparent">
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-primary/10 blur-3xl" />
          </div>
          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wide mb-4">
              <Users className="h-3.5 w-3.5" /> Nuevo
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-3">
              Invita y Gana Premium Gratis
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-10">
              Comparte tu enlace de SnapSite. Cada persona que se registra con él te suma un punto — a los{" "}
              <strong className="text-foreground">5 referidos, Premium se activa solo</strong>, sin pagar nada.
              Sigue invitando y desbloquea insignias que se lucen en tu perfil para siempre.
            </p>

            <div className="grid grid-cols-5 gap-1.5 sm:gap-4 max-w-3xl mx-auto mb-10">
              {[
                { slug: 'premium', label: 'Premium', points: 5 },
                { slug: 'bronce', label: 'Bronce', points: 15 },
                { slug: 'plata', label: 'Plata', points: 50 },
                { slug: 'oro', label: 'Oro', points: 150 },
                { slug: 'platino', label: 'Platino', points: 500 },
              ].map((badge) => (
                <div
                  key={badge.slug}
                  className="group flex flex-col items-center gap-1 sm:gap-2 hover:-translate-y-1 transition-transform duration-300"
                >
                  <Image
                    src={`/badges/${badge.slug}_sombra.svg`}
                    alt={`Insignia ${badge.label}`}
                    width={56}
                    height={56}
                    className="w-9 h-9 sm:w-14 sm:h-14 transition-transform duration-300 group-hover:scale-110"
                  />
                  <span className="text-[0.65rem] sm:text-sm font-bold text-foreground text-center leading-tight">{badge.label}</span>
                  <span className="text-[0.6rem] sm:text-xs text-muted-foreground text-center leading-tight">{badge.points} ref.</span>
                </div>
              ))}
            </div>

            <Button asChild size="lg" className="group text-base h-12 shadow-lg shadow-primary/20 px-8">
              <Link href="/crear">
                <Sparkles className="mr-2 h-5 w-5 transition-transform group-hover:scale-110" />
                Crear mi SnapSite y empezar a invitar
              </Link>
            </Button>
          </div>
        </section>

        {/* ── SECCIÓN CROSS-SELL .SITE DE RTSI ── */}
        <section className="relative w-full py-12 px-4">
          <div className="max-w-4xl mx-auto p-8 rounded-3xl border bg-card/50 backdrop-blur-sm flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
            <div className="space-y-2">
              <h3 className="text-2xl font-bold">¿Quieres un sitio web completo?</h3>
              <p className="text-muted-foreground">En <strong>.Site de RTSI</strong> lo hacemos por ti. De tu tarjeta digital a una presencia web profesional y total.</p>
            </div>
            <Button variant="outline" size="lg" asChild className="shrink-0 border-primary/50 hover:bg-primary/10">
              <a href="https://rtsi.site" target="_blank" rel="noopener noreferrer">Saber más en .Site</a>
            </Button>
          </div>
        </section>

        {/* ── BOTTOM CTA ── */}
        <section className="relative w-full py-16 px-4 text-center bg-background overflow-hidden">
          {/* Orbes de fondo */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[300px] rounded-full bg-primary/15 blur-3xl" />
          </div>
          <div className="relative z-10 max-w-md mx-auto space-y-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">¿Listo para brillar?</h2>
            <p className="text-muted-foreground">Crea tu tarjeta digital en menos de 5 minutos. Gratis, sin tarjeta de crédito.</p>
            <Button asChild size="lg" className="w-full group text-base h-12 shadow-lg shadow-primary/20">
              <Link href="/dashboard">
                <Sparkles className="mr-2 h-5 w-5 transition-transform group-hover:scale-110" />
                Empezar ahora — Es gratis
              </Link>
            </Button>
          </div>
        </section>

        {/* ── FOOTER ── */}
        <footer className="w-full border-t border-border py-8 px-4">
          <div className="max-w-4xl mx-auto flex flex-col items-center gap-4">
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2">
              {footerLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-xs text-muted-foreground hover:text-primary hover:underline transition-colors"
                >
                  {link.label}
                </Link>
              ))}
              <a href="mailto:snapsite@rtsi.mx" className="text-xs text-muted-foreground hover:text-primary hover:underline transition-colors">
                Contacto
              </a>
            </div>
            <p className="text-xs text-muted-foreground/70 text-center">
              © {new Date().getFullYear()} SnapSite de{' '}
              <a href="https://rtsi.site" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline transition-colors">
                .Site RTSI.
              </a>{' '}
              Todos los derechos reservados.
            </p>
          </div>
        </footer>
      </main>

      <TutorialCarousel isOpen={isTutorialOpen} onClose={() => setIsTutorialOpen(false)} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdWebApp) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdOrg) }} />
    </>
  );
}
